import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Building2, TrendingUp, Shield, Users, Globe, 
  Zap, BarChart3, Lock, CheckCircle2, ArrowRight, FileText
} from 'lucide-react';

const PARTICIPANT_LOGOS = [
  { name: 'Africa Finance Corp', initials: 'AFC' },
  { name: 'Afreximbank', initials: 'AFRX' },
  { name: 'Abu Dhabi Investment', initials: 'ADIA' },
  { name: 'Mubadala', initials: 'MBD' },
  { name: 'IFC World Bank', initials: 'IFC' },
  { name: 'Standard Chartered', initials: 'SC' },
  { name: 'FirstRand Group', initials: 'FRG' },
  { name: 'Anglo American', initials: 'AAL' },
  { name: 'ACWA Power', initials: 'ACWA' },
  { name: 'Masdar Energy', initials: 'MSD' },
  { name: 'TotalEnergies', initials: 'TE' },
  { name: 'Glencore PLC', initials: 'GLC' },
  { name: 'SABIC', initials: 'SBC' },
  { name: 'DP World', initials: 'DPW' },
  { name: 'Dangote Group', initials: 'DNG' },
  { name: 'Sibanye Stillwater', initials: 'SSW' },
];

export function LandingPage() {
  const navigate = useNavigate();
  const [showLegal, setShowLegal] = useState(false);

  const stats = [
    { label: 'Total Deal Flow', value: '$3.1B', icon: TrendingUp },
    { label: 'Active Investors', value: '250+', icon: Users },
    { label: 'Countries', value: '15', icon: Globe },
    { label: 'Successful Closes', value: '47', icon: CheckCircle2 }
  ];

  return (
    <div className="min-h-screen bg-background">
      <section className="relative border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex items-center justify-between mb-16">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-accent rounded flex items-center justify-center">
                <span className="text-white font-bold text-sm">INMX</span>
              </div>
              <span className="text-2xl font-bold">Infrastructure & Natural Minerals Exchange</span>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login">
                Sign In
              </Button>
              <Button onClick={() => navigate('/register')} data-testid="nav-register">
                Get Started
              </Button>
            </div>
          </div>

          <div className="max-w-3xl">
            <h1 className="text-6xl font-bold tracking-tight mb-6" data-testid="hero-headline">
              Infrastructure.<br />Natural Minerals.<br />
              <span className="text-accent">Market Intelligence.</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              INMX is a digital market infrastructure platform connecting institutional investors, 
              project sponsors, commodity buyers, and governments across Middle East and Africa — 
              with energy infrastructure and minerals deal flow, live market intelligence, AI-enabled deal scoring, and compliance-grade workflows.
            </p>
            <div className="flex items-center space-x-4">
              <Button size="lg" onClick={() => navigate('/register')} data-testid="hero-cta">
                Request Access <ArrowRight size={16} className="ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/login')}>
                Existing User
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-6 mt-16">
            {stats.map((stat, index) => (
              <Card key={index}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <stat.icon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="text-3xl font-bold font-mono mb-1">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Participant Logo Marquee */}
      <section className="border-b border-border py-6 overflow-hidden bg-muted/30" data-testid="logo-marquee">
        <p className="text-center text-[10px] text-muted-foreground uppercase tracking-widest mb-4">Trusted by leading institutions across MEA</p>
        <div className="relative">
          <div className="flex animate-marquee whitespace-nowrap">
            {[...PARTICIPANT_LOGOS, ...PARTICIPANT_LOGOS].map((logo, i) => (
              <div key={i} className="flex items-center justify-center mx-6 flex-shrink-0">
                <div className="w-14 h-14 rounded bg-background border border-border flex items-center justify-center hover:border-foreground/30 transition-colors">
                  <span className="font-mono text-[10px] font-bold text-muted-foreground">{logo.initials}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-lg font-bold mb-4">Ready to Close Your Next Deal?</h2>
          <p className="text-base text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join infrastructure investors, project sponsors, and commodity buyers who trust INMX.
          </p>
          <Button size="lg" onClick={() => navigate('/register')} data-testid="footer-cta">
            Request Platform Access
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            Institutional accounts only - Verification required
          </p>
        </div>
      </section>

      <footer className="border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="border-t border-border mt-12 pt-8 flex items-center justify-between text-sm text-muted-foreground">
            <p>2026 Infrastructure & Natural Minerals Exchange. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <button onClick={() => setShowLegal(true)} className="hover:text-foreground transition-colors cursor-pointer underline-offset-4 hover:underline" data-testid="privacy-link">
                Privacy Policy & Legal
              </button>
              <p>Registered in the UAE - Regulated Platform</p>
            </div>
          </div>
        </div>
      </footer>

      <Dialog open={showLegal} onOpenChange={setShowLegal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText size={18} />Privacy Policy & Legal Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 text-sm text-muted-foreground leading-relaxed">
            <div>
              <h3 className="font-semibold text-foreground mb-2">1. Data Privacy</h3>
              <p>INMX is committed to protecting the privacy and security of personal data. All personal information collected through this platform is processed in accordance with applicable data protection laws, including UAE Federal Decree-Law No. 45 of 2021 concerning Personal Data Protection.</p>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-2">2. Data Collection</h3>
              <p>We collect information you provide during registration, including name, email, organization details, and investment preferences. This data is used to facilitate deal matching, platform access, and communication between participants.</p>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-2">3. Confidentiality</h3>
              <p>All deal information, financial data, and commercial terms shared on the platform are treated as strictly confidential. Access to deal documents is controlled through our Secure Data Room with role-based permissions.</p>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-2">4. Intellectual Property</h3>
              <p>All content, data, and proprietary analytics on INMX are the intellectual property of Infrastructure & Natural Minerals Exchange. Unauthorized reproduction or distribution is prohibited.</p>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-2">5. Disclaimer</h3>
              <p>INMX provides market intelligence and deal matching services only. INMX does not provide investment advice, nor does it guarantee the accuracy of third-party data. All investment decisions are the sole responsibility of the participants.</p>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-2">6. Governing Law</h3>
              <p>This platform and its terms are governed by the laws of the United Arab Emirates. Any disputes shall be resolved through the courts of Abu Dhabi, UAE.</p>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-2">7. Contact</h3>
              <p>For any privacy or legal inquiries, please contact: legal@inmx.com</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
      `}</style>
    </div>
  );
}
